import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RatingStatusBadge } from "@/components/RatingStatusBadge";
import { type FeedbackState } from "@/components/FeedbackButtons";
import { MiniScoreDial } from "@/components/MiniScoreDial";

import type { CompanyResult } from "@/types/api";

/**
 * Build a display address from a CompanyResult, avoiding duplication.
 *
 * The backend stores the full raw address string (e.g. "Wexstraße 7, 20355 Hamburg")
 * AND the parsed postal_code/city separately. Naively concatenating them produces
 * duplicates like "Wexstraße 7, 20355 Hamburg, 20355 Hamburg".
 *
 * Strategy: split address by commas into segments, check if postal_code+city already
 * appears as a segment. Only append components that are genuinely missing.
 */
export function formatAddress(
  result: Pick<CompanyResult, "address" | "postal_code" | "city">,
): string {
  const { address, postal_code, city } = result;

  // Nothing at all
  if (!address && !postal_code && !city) return "—";

  // No raw address — build from components only
  if (!address) {
    return [postal_code, city].filter(Boolean).join(" ");
  }

  // Raw address exists — check which components are already present as segments
  const segments = address.split(",").map((s) => s.trim());
  const cityPlz = [postal_code, city].filter(Boolean).join(" "); // e.g. "20355 Hamburg"

  const cityPlzPresent =
    cityPlz.length > 0 &&
    segments.some((seg) => seg === cityPlz || seg.includes(cityPlz));

  // If the combined "PLZ City" is already a segment, address is complete
  if (cityPlzPresent) return address;

  // Check individual components as segments
  const postalPresent =
    !!postal_code && segments.some((seg) => seg.includes(postal_code));
  const cityPresent =
    !!city && segments.some((seg) => seg === city || seg.endsWith(` ${city}`));

  // Both individually present (e.g. different segments) — address is complete
  if ((postalPresent || !postal_code) && (cityPresent || !city)) return address;

  // Append only what's missing
  const missing = [
    !postalPresent ? postal_code : null,
    !cityPresent ? city : null,
  ]
    .filter(Boolean)
    .join(" ");

  return missing ? `${address}, ${missing}` : address;
}

function ExpandableDescription({ text }: { text: string }) {
  const { t } = useTranslation("results");
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 500;
  const truncated = isLong
    ? (() => {
        const firstSentence = text.match(/^[^.!?]+[.!?]/);
        if (firstSentence && firstSentence[0].length <= 500)
          return firstSentence[0];
        return text.slice(0, 500).trimEnd() + "…";
      })()
    : text;

  return (
    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
      {expanded ? text : truncated}
      {isLong && (
        <button
          className="ml-1 text-primary hover:underline font-medium text-sm"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? t("card.showLess") : t("card.showMore")}
        </button>
      )}
    </p>
  );
}

interface ResultCardProps {
  result: CompanyResult;
  expanded?: boolean;
  onToggle: (resultId: string) => void;
  feedbackState?: FeedbackState;
}

export function ResultCard({
  result,
  expanded,
  onToggle,
  feedbackState,
}: ResultCardProps) {
  const { t } = useTranslation("results");
  const handleCardClick = () => onToggle(result.result_id);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle(result.result_id);
    }
  };

  const keywords = result.matched_keywords ?? [];
  const visibleKeywords = keywords.slice(0, 3);
  const overflowCount = keywords.length - 3;

  return (
    <div
      className={`rounded-lg border bg-card shadow-card hover:shadow-md transition-all cursor-pointer ${expanded ? "shadow-md bg-primary/5 ring-1 ring-primary/15" : ""}`}
      aria-expanded={expanded}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="article"
      aria-label={t("card.ariaLabel", {
        name: result.company_name || t("card.unknownCompany"),
      })}
    >
      <div className="flex items-stretch">
        {/* Score dial column */}
        {result.llm_relevance_score != null && (
          <div className="w-14 shrink-0 flex items-center justify-center border-r border-border py-4 px-1">
            <MiniScoreDial score={result.llm_relevance_score} />
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0 p-4">
          {/* Company name */}
          <h3 className="text-base font-semibold truncate">
            {result.company_name || t("card.unknownCompany")}
          </h3>

          {/* Address */}
          <p className="text-sm text-muted-foreground truncate mt-0.5">
            {formatAddress(result)}
          </p>

          {/* Description */}
          {result.description && (
            <ExpandableDescription text={result.description} />
          )}

          {/* Keyword chips */}
          {visibleKeywords.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {visibleKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-success/10 text-success"
                >
                  {kw}
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t("card.keywordOverflow", { count: overflowCount })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right meta column */}
        <div className="shrink-0 flex flex-col items-end justify-between p-4 pl-2">
          <RatingStatusBadge feedbackState={feedbackState} />
          <span
            aria-hidden="true"
            className="text-xs text-muted-foreground mt-2"
          >
            {expanded ? t("card.collapse") : t("card.details")}
          </span>
        </div>
      </div>
    </div>
  );
}
